import * as React from 'react'

import {
  Button,
  XIcon,
  CrosshairIcon,
  Flex,
  IconButton,
  Input,
  Link,
  Text,
  UnstyledButton,
  UserRoundPlusIcon,
  useIsMobileScreenSize,
  VisuallyHidden,
  WarningIcon,
} from '@buffer-mono/popcorn'
import type { NormalizedImagePoint } from '@buffer-mono/popcorn'

import type {
  ImageWorkspaceUserTagInput,
  ImageWorkspaceUserTagProjection,
} from '../imageWorkspaceSession'

import styles from './ComposerImageUserTagging.module.css'

type ComposerImageUserTaggingBehaviorProps = Readonly<{
  maxTagCount: number
  /** Staged tags across every attached image, including this one. */
  totalTagCount: number
  disabled?: boolean
  /** Prevents spatial placement while retaining existing tag management. */
  placementDisabled?: boolean
  onInteractionStateChange?: (
    state: ComposerImageUserTaggingInteractionState,
  ) => void
}>

type ComposerImageUserTaggingProps = ComposerImageUserTaggingBehaviorProps &
  Readonly<{
    tags: readonly ImageWorkspaceUserTagProjection[]
    onAddTag: (tag: ImageWorkspaceUserTagInput) => void
    onMoveTag: (anchorIndex: number, point: NormalizedImagePoint) => void
    onRemoveTag: (anchorIndex: number) => void
    children: (slots: ComposerImageUserTaggingSlots) => React.ReactNode
  }>

type ComposerImageUserTaggingSlots = Readonly<{
  imageOverlay: React.ReactNode
  cropOverlay: React.ReactNode
  stageOverlay: React.ReactNode
  controls: React.ReactNode
  isPlacementActive: boolean
}>

export type ComposerImageUserTaggingHandle = Readonly<{
  cancelPlacement: (focusTarget?: 'action' | 'heading' | null) => boolean
  focusSection: () => void
  revealSection: (options?: Readonly<{ onlyIfFocused?: boolean }>) => void
  startPlacement: () => boolean
}>

export type ComposerImageUserTaggingInteractionState = Readonly<{
  isPlacementActive: boolean
  isEnteringUsername: boolean
}>

type TagPosition = Readonly<{
  x: number
  y: number
}>

type TagPlacement =
  | Readonly<{ state: 'idle' }>
  | Readonly<{
      state: 'choosing-position'
      mode: 'add'
      position: TagPosition
    }>
  | Readonly<{
      state: 'choosing-position'
      mode: 'move'
      position: TagPosition
      showPositionPreview: boolean
      tag: Readonly<{
        anchorIndex: number
        listIndex: number
        username: string
      }>
    }>
  | Readonly<{
      state: 'entering-username'
      position: TagPosition
    }>

type RemovalFocus = Readonly<
  { kind: 'entry' } | { kind: 'remove'; listIndex: number }
>

type FocusAfterClose =
  | 'action'
  | 'heading'
  | Readonly<{ kind: 'move'; listIndex: number }>
  | null

type UsernameInputError = 'duplicate' | 'invalid' | null

const normalizeInstagramUsername = (value: string): string =>
  value.replace(/@/g, '').replace(/\s/g, '')

const INSTAGRAM_USERNAME_MAX_LENGTH = 30
const INSTAGRAM_USERNAME_PATTERN = /^[A-Za-z0-9._]+$/
const isValidInstagramUsername = (value: string): boolean =>
  value.length > 0 &&
  value.length <= INSTAGRAM_USERNAME_MAX_LENGTH &&
  INSTAGRAM_USERNAME_PATTERN.test(value)

const getInstagramProfileUrl = (username: string): string =>
  `https://www.instagram.com/${encodeURIComponent(username)}/`

const CENTER_POSITION: TagPosition = { x: 0.5, y: 0.5 }
const POSITION_STEP = 0.01
const LARGE_POSITION_STEP = 0.1
const TAGGING_SECTION_HEADING = 'Add User Tags'
const INSTAGRAM_LIMIT_MESSAGE =
  'Instagram API limitations prevent searching for usernames and tagging private users.'
const IDLE_INTERACTION_STATE: ComposerImageUserTaggingInteractionState = {
  isPlacementActive: false,
  isEnteringUsername: false,
}

const clampCoordinate = (value: number): number =>
  Math.min(1, Math.max(0, value))

const getTagLabelTranslation = (value: number): string => {
  if (value <= 0.15) return '0%'
  if (value >= 0.85) return '-100%'
  return '-50%'
}

const getTagLabelTransform = (
  point: Readonly<{ x: number; y: number }>,
): string =>
  `translate(${getTagLabelTranslation(point.x)}, ${getTagLabelTranslation(point.y)})`

const getTagLabelVerticalSide = (value: number): 'top' | 'middle' | 'bottom' =>
  value <= 0.15 ? 'top' : value >= 0.85 ? 'bottom' : 'middle'

const getTagPosition = (
  event: React.MouseEvent<HTMLButtonElement>,
): TagPosition | null => {
  const bounds = event.currentTarget.getBoundingClientRect()
  if (bounds.width <= 0 || bounds.height <= 0) return null

  return {
    x: clampCoordinate((event.clientX - bounds.left) / bounds.width),
    y: clampCoordinate((event.clientY - bounds.top) / bounds.height),
  }
}

const moveTagPosition = (
  position: TagPosition,
  key: string,
  step: number,
): TagPosition => {
  return {
    x: clampCoordinate(
      position.x +
        (key === 'ArrowRight' ? step : key === 'ArrowLeft' ? -step : 0),
    ),
    y: clampCoordinate(
      position.y + (key === 'ArrowDown' ? step : key === 'ArrowUp' ? -step : 0),
    ),
  }
}

const quantizeTagPosition = (position: TagPosition): TagPosition => ({
  x: Number(position.x.toFixed(2)),
  y: Number(position.y.toFixed(2)),
})

export const ComposerImageUserTagging = React.forwardRef<
  ComposerImageUserTaggingHandle,
  ComposerImageUserTaggingProps
>(
  (
    {
      tags,
      onAddTag,
      onMoveTag,
      onRemoveTag,
      maxTagCount,
      totalTagCount,
      disabled = false,
      placementDisabled = false,
      onInteractionStateChange,
      children,
    },
    forwardedRef,
  ) => {
    const [placement, setPlacement] = React.useState<TagPlacement>({
      state: 'idle',
    })
    const [username, setUsername] = React.useState('')
    const [inputError, setInputError] = React.useState<UsernameInputError>(null)
    const [hoveredListAnchorIndex, setHoveredListAnchorIndex] = React.useState<
      number | null
    >(null)
    const [hoveredMarkerAnchorIndex, setHoveredMarkerAnchorIndex] =
      React.useState<number | null>(null)
    const [focusedAnchorIndex, setFocusedAnchorIndex] = React.useState<
      number | null
    >(null)
    const [cropRecoveryAnnouncement, setCropRecoveryAnnouncement] =
      React.useState('')
    const [tagMoveAnnouncement, setTagMoveAnnouncement] = React.useState({
      key: 0,
      text: '',
    })
    const [tagPositionAnnouncement, setTagPositionAnnouncement] =
      React.useState({ key: 0, text: '' })
    const isMobileScreenSize = useIsMobileScreenSize()
    const imageOverlayRef = React.useRef<HTMLDivElement>(null)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const positionButtonRef = React.useRef<HTMLButtonElement>(null)
    const taggingActionRef = React.useRef<HTMLButtonElement>(null)
    const headingRef = React.useRef<HTMLHeadingElement>(null)
    const moveButtonRefs = React.useRef<Array<HTMLButtonElement | null>>([])
    const removeButtonRefs = React.useRef<Array<HTMLButtonElement | null>>([])
    const focusCanvasAfterOpenRef = React.useRef(false)
    const focusAfterCloseRef = React.useRef<FocusAfterClose>(null)
    const removalFocusRef = React.useRef<RemovalFocus | null>(null)
    const previousOutsideCropCountRef = React.useRef<number | null>(null)
    const inputId = React.useId()
    const inputErrorId = React.useId()
    const instagramLimitMessageId = React.useId()
    const headingId = React.useId()
    const placementKeyboardDescriptionId = React.useId()
    const reachedMaxTagCount = totalTagCount >= maxTagCount
    const canChoosePosition =
      !disabled && !placementDisabled && !reachedMaxTagCount
    const canMoveTag = !disabled && !placementDisabled
    const isPlacementActive = placement.state !== 'idle'
    const isEnteringUsername = placement.state === 'entering-username'
    const isMovingTag =
      placement.state === 'choosing-position' && placement.mode === 'move'
    const positionPreviewIsVisible =
      isEnteringUsername ||
      (placement.state === 'choosing-position' &&
        (placement.mode === 'move'
          ? placement.showPositionPreview
          : placement.position.x !== CENTER_POSITION.x ||
            placement.position.y !== CENTER_POSITION.y))
    const highlightedAnchorIndex =
      focusedAnchorIndex ?? hoveredMarkerAnchorIndex ?? hoveredListAnchorIndex
    const outsideCropCount = tags.filter(
      (tag) => tag.state === 'outside',
    ).length
    const hasPositionedTags = tags.some((tag) => tag.state !== 'invalid')

    const focusTaggingEntryPoint = React.useCallback((): void => {
      if (canChoosePosition && taggingActionRef.current) {
        taggingActionRef.current.focus()
        return
      }

      headingRef.current?.focus()
    }, [canChoosePosition])

    const closePlacement = React.useCallback(
      (focusTarget: FocusAfterClose): void => {
        focusAfterCloseRef.current = focusTarget
        if (
          placement.state === 'choosing-position' &&
          placement.mode === 'move'
        ) {
          setTagMoveAnnouncement((currentAnnouncement) => ({
            key: currentAnnouncement.key + 1,
            text: `Kept @${placement.tag.username} in the same position.`,
          }))
        }
        setPlacement({ state: 'idle' })
        setUsername('')
        setInputError(null)
      },
      [placement],
    )

    const revealSection = React.useCallback(
      (options?: Readonly<{ onlyIfFocused?: boolean }>): void => {
        const heading = headingRef.current
        if (!heading) return
        if (options?.onlyIfFocused && document.activeElement !== heading) return

        if (typeof heading.scrollIntoView === 'function') {
          heading.scrollIntoView({
            block: isMobileScreenSize ? 'start' : 'nearest',
            inline: 'nearest',
          })
        }
      },
      [isMobileScreenSize],
    )

    const focusSection = React.useCallback((): void => {
      const heading = headingRef.current
      if (!heading) return

      revealSection()
      heading.focus({ preventScroll: true })
    }, [revealSection])

    const startPlacement = React.useCallback(
      (focusCanvas: boolean): boolean => {
        if (isPlacementActive || !canChoosePosition) return false

        focusCanvasAfterOpenRef.current = focusCanvas
        setPlacement({
          state: 'choosing-position',
          mode: 'add',
          position: CENTER_POSITION,
        })
        setUsername('')
        setInputError(null)
        return true
      },
      [canChoosePosition, isPlacementActive],
    )

    const cancelPendingTag = React.useCallback((): boolean => {
      if (!isEnteringUsername) return false

      focusCanvasAfterOpenRef.current = true
      setPlacement({
        state: 'choosing-position',
        mode: 'add',
        position: CENTER_POSITION,
      })
      setUsername('')
      setInputError(null)
      return true
    }, [isEnteringUsername])

    React.useImperativeHandle(
      forwardedRef,
      () => ({
        cancelPlacement: (focusTarget): boolean => {
          if (!isPlacementActive) return false
          if (focusTarget === undefined && cancelPendingTag()) return true

          const resolvedFocusTarget: FocusAfterClose =
            focusTarget === undefined
              ? isMovingTag
                ? { kind: 'move', listIndex: placement.tag.listIndex }
                : canChoosePosition
                  ? 'action'
                  : 'heading'
              : focusTarget
          closePlacement(resolvedFocusTarget)
          return true
        },
        focusSection,
        revealSection,
        startPlacement: (): boolean => startPlacement(true),
      }),
      [
        canChoosePosition,
        cancelPendingTag,
        closePlacement,
        focusSection,
        isPlacementActive,
        isMovingTag,
        placement,
        revealSection,
        startPlacement,
      ],
    )

    React.useEffect(() => {
      if (!placementDisabled || !isPlacementActive) return

      closePlacement('heading')
    }, [closePlacement, isPlacementActive, placementDisabled])

    React.useEffect(() => {
      const clearMarkerHover = (): void => setHoveredMarkerAnchorIndex(null)
      const handleMouseMove = (event: MouseEvent): void => {
        const overlay = imageOverlayRef.current
        let nextAnchorIndex: number | null = null

        if (overlay) {
          for (let index = 0; index < overlay.children.length; index += 1) {
            const marker = overlay.children.item(index)
            if (!(marker instanceof HTMLElement)) continue

            const bounds = marker.getBoundingClientRect()
            const pointerIsOverMarker =
              event.clientX >= bounds.left &&
              event.clientX <= bounds.right &&
              event.clientY >= bounds.top &&
              event.clientY <= bounds.bottom
            if (!pointerIsOverMarker) continue

            const anchorIndex = Number(marker.dataset.tagAnchorIndex)
            nextAnchorIndex = Number.isInteger(anchorIndex) ? anchorIndex : null
            break
          }
        }

        setHoveredMarkerAnchorIndex((currentAnchorIndex) =>
          currentAnchorIndex === nextAnchorIndex
            ? currentAnchorIndex
            : nextAnchorIndex,
        )
      }
      const handleMouseOut = (event: MouseEvent): void => {
        if (event.relatedTarget === null) clearMarkerHover()
      }

      document.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseout', handleMouseOut)
      window.addEventListener('blur', clearMarkerHover)

      return (): void => {
        document.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseout', handleMouseOut)
        window.removeEventListener('blur', clearMarkerHover)
      }
    }, [])

    React.useEffect(() => {
      const previousOutsideCropCount = previousOutsideCropCountRef.current
      previousOutsideCropCountRef.current = outsideCropCount

      if (
        previousOutsideCropCount === null ||
        outsideCropCount > 0 ||
        !hasPositionedTags
      ) {
        setCropRecoveryAnnouncement('')
        return
      }

      if (previousOutsideCropCount > 0) {
        setCropRecoveryAnnouncement('All tags are inside the crop.')
      }
    }, [hasPositionedTags, outsideCropCount])

    React.useEffect(() => {
      if (placement.state === 'entering-username') inputRef.current?.focus()
    }, [placement.state])

    React.useEffect(() => {
      onInteractionStateChange?.({ isPlacementActive, isEnteringUsername })
    }, [isEnteringUsername, isPlacementActive, onInteractionStateChange])

    React.useEffect(
      () => (): void => onInteractionStateChange?.(IDLE_INTERACTION_STATE),
      [onInteractionStateChange],
    )

    React.useEffect(() => {
      if (placement.state !== 'choosing-position') return

      const positionButton = positionButtonRef.current
      if (
        isMobileScreenSize &&
        positionButton &&
        typeof positionButton.scrollIntoView === 'function'
      ) {
        positionButton.scrollIntoView({ block: 'center', inline: 'nearest' })
      }

      if (!focusCanvasAfterOpenRef.current) return

      focusCanvasAfterOpenRef.current = false
      positionButton?.focus({ preventScroll: true })
    }, [isMobileScreenSize, placement.state])

    React.useEffect(() => {
      if (isPlacementActive || !focusAfterCloseRef.current) return

      const focusTarget = focusAfterCloseRef.current
      if (focusTarget === 'action') {
        focusTaggingEntryPoint()
      } else if (focusTarget === 'heading') {
        headingRef.current?.focus()
      } else {
        const moveButton = moveButtonRefs.current[focusTarget.listIndex] ?? null
        if (moveButton) {
          moveButton.focus()
        } else {
          focusTaggingEntryPoint()
        }
      }
      focusAfterCloseRef.current = null
    }, [focusTaggingEntryPoint, isPlacementActive, tags])

    React.useEffect(() => {
      const removalFocus = removalFocusRef.current
      if (!removalFocus) return

      removalFocusRef.current = null
      if (removalFocus.kind === 'entry') {
        focusTaggingEntryPoint()
        return
      }

      const removeButton =
        removeButtonRefs.current[removalFocus.listIndex] ?? null
      if (removeButton) {
        removeButton.focus()
      } else {
        focusTaggingEntryPoint()
      }
    }, [focusTaggingEntryPoint, tags])

    const handleTaggingAction = (
      event: React.MouseEvent<HTMLButtonElement>,
    ): void => {
      if (isPlacementActive) {
        closePlacement(
          isMovingTag
            ? { kind: 'move', listIndex: placement.tag.listIndex }
            : 'action',
        )
        return
      }

      startPlacement(event.detail === 0)
    }

    const beginTagMove = (
      event: React.MouseEvent<HTMLButtonElement>,
      tag: ImageWorkspaceUserTagProjection,
      listIndex: number,
    ): void => {
      if (tag.state === 'invalid' || isPlacementActive || !canMoveTag) return

      const position =
        tag.state === 'inside'
          ? { x: tag.point.x, y: tag.point.y }
          : CENTER_POSITION

      focusCanvasAfterOpenRef.current = event.detail === 0
      setHoveredListAnchorIndex(null)
      setHoveredMarkerAnchorIndex(null)
      setFocusedAnchorIndex(null)
      setPlacement({
        state: 'choosing-position',
        mode: 'move',
        position,
        showPositionPreview: false,
        tag: {
          anchorIndex: tag.anchorIndex,
          listIndex,
          username: tag.username,
        },
      })
      setUsername(tag.username)
      setInputError(null)
      setTagMoveAnnouncement((currentAnnouncement) => ({
        key: currentAnnouncement.key + 1,
        text: `Select a new position for @${tag.username}.`,
      }))
    }

    const commitTagMove = (position: TagPosition): void => {
      if (!isMovingTag || !canMoveTag) return

      focusAfterCloseRef.current = {
        kind: 'move',
        listIndex: placement.tag.listIndex,
      }
      onMoveTag(placement.tag.anchorIndex, position)
      setPlacement({ state: 'idle' })
      setUsername('')
      setInputError(null)
      setTagMoveAnnouncement((currentAnnouncement) => ({
        key: currentAnnouncement.key + 1,
        text: `Moved @${placement.tag.username}.`,
      }))
    }

    const handlePositionClick = (
      event: React.MouseEvent<HTMLButtonElement>,
    ): void => {
      if (placement.state === 'idle') return

      const nextPosition =
        event.detail === 0 ? placement.position : getTagPosition(event)
      if (!nextPosition) return

      if (isMovingTag) {
        commitTagMove(nextPosition)
        return
      }

      setPlacement({ state: 'entering-username', position: nextPosition })
      if (placement.state === 'choosing-position') {
        setInputError(null)
      } else {
        inputRef.current?.focus()
      }
    }

    const handlePositionKeyDown = (
      event: React.KeyboardEvent<HTMLButtonElement>,
    ): void => {
      if (placement.state === 'idle') return

      if (
        event.key === 'ArrowUp' ||
        event.key === 'ArrowRight' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowLeft'
      ) {
        event.preventDefault()
        const step = event.shiftKey ? LARGE_POSITION_STEP : POSITION_STEP
        const nextPosition = moveTagPosition(
          placement.position,
          event.key,
          step,
        )
        setPlacement(
          placement.state === 'choosing-position' && placement.mode === 'move'
            ? {
                ...placement,
                position: nextPosition,
                showPositionPreview: true,
              }
            : { ...placement, position: nextPosition },
        )
        setTagPositionAnnouncement((currentAnnouncement) => ({
          key: currentAnnouncement.key + 1,
          text: `Tag position: ${Math.round(nextPosition.x * 100)}% from the left, ${Math.round(nextPosition.y * 100)}% from the top.`,
        }))
        return
      }

      if (
        placement.state === 'choosing-position' &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault()
        if (placement.mode === 'move') {
          commitTagMove(placement.position)
          return
        }
        setPlacement({
          state: 'entering-username',
          position: placement.position,
        })
      }
    }

    const addPendingTag = (): void => {
      const normalizedUsername = normalizeInstagramUsername(username)

      if (
        placement.state !== 'entering-username' ||
        disabled ||
        placementDisabled ||
        reachedMaxTagCount
      ) {
        return
      }

      if (!isValidInstagramUsername(normalizedUsername)) {
        setInputError('invalid')
        return
      }

      const usernameAlreadyAdded = tags.some(
        (tag) =>
          tag.username.toLowerCase() === normalizedUsername.toLowerCase(),
      )
      if (usernameAlreadyAdded) {
        setInputError('duplicate')
        return
      }

      const tag: ImageWorkspaceUserTagInput = {
        username: normalizedUsername,
        point: quantizeTagPosition(placement.position),
      }
      onAddTag(tag)

      if (totalTagCount + 1 < maxTagCount) {
        focusCanvasAfterOpenRef.current = true
        setPlacement({
          state: 'choosing-position',
          mode: 'add',
          position: CENTER_POSITION,
        })
        setUsername('')
        setInputError(null)
        return
      }

      closePlacement('heading')
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault()
      addPendingTag()
    }

    const clearFocusedTag = (anchorIndex: number): void => {
      setFocusedAnchorIndex((currentAnchorIndex) =>
        currentAnchorIndex === anchorIndex ? null : currentAnchorIndex,
      )
    }

    const removeTag = (anchorIndex: number, listIndex: number): void => {
      if (disabled) return

      setHoveredListAnchorIndex(null)
      setHoveredMarkerAnchorIndex(null)
      setFocusedAnchorIndex(null)
      const nextTagCount = tags.length - 1
      removalFocusRef.current =
        nextTagCount > 0
          ? {
              kind: 'remove',
              listIndex: Math.min(listIndex, nextTagCount - 1),
            }
          : { kind: 'entry' }
      onRemoveTag(anchorIndex)
    }

    const placementInstruction = isMovingTag
      ? `Select a new position for @${placement.tag.username}.`
      : isEnteringUsername
        ? 'Enter their Instagram username, or select the image to move the tag.'
        : 'Select where the person appears in the image.'
    const placementHint = isMovingTag ? 'Choose new position' : 'Place tag'

    const imageOverlay = (
      <div ref={imageOverlayRef} className={styles.imageOverlay}>
        {tags.map((tag, index) => {
          if (tag.state === 'invalid' || tag.imagePoint === undefined) {
            return null
          }

          const isHighlighted = highlightedAnchorIndex === tag.anchorIndex

          return (
            <span
              key={`${tag.anchorIndex}-${tag.username}`}
              className={styles.tagLabel}
              data-tag-anchor-index={tag.anchorIndex}
              data-highlighted={isHighlighted ? 'true' : undefined}
              data-label-side={tag.imagePoint.x >= 0.5 ? 'left' : 'right'}
              data-label-vertical={getTagLabelVerticalSide(tag.imagePoint.y)}
              data-tag-state={tag.state}
              style={{
                left: `${tag.imagePoint.x * 100}%`,
                top: `${tag.imagePoint.y * 100}%`,
                transform: getTagLabelTransform(tag.imagePoint),
              }}
              aria-hidden="true"
            >
              {index + 1}
              {isHighlighted ? (
                <span className={styles.tagMarkerUsername}>
                  @{tag.username}
                </span>
              ) : null}
            </span>
          )
        })}
      </div>
    )

    const cropOverlay = (
      <div
        className={styles.cropOverlay}
        data-testid="tag-crop-overlay"
        data-placement-active={isPlacementActive ? 'true' : undefined}
      >
        {isPlacementActive ? (
          <UnstyledButton
            ref={positionButtonRef}
            type="button"
            className={styles.positionButton}
            aria-label={placementInstruction}
            aria-describedby={placementKeyboardDescriptionId}
            disabled={isMovingTag ? !canMoveTag : !canChoosePosition}
            onClick={handlePositionClick}
            onKeyDown={handlePositionKeyDown}
          />
        ) : null}

        {isPlacementActive ? (
          <VisuallyHidden id={placementKeyboardDescriptionId}>
            Use the arrow keys to move the tag. Press Escape to cancel.
          </VisuallyHidden>
        ) : null}

        {positionPreviewIsVisible ? (
          <span
            className={styles.positionMarker}
            data-testid="pending-tag-position"
            style={{
              left: `${placement.position.x * 100}%`,
              top: `${placement.position.y * 100}%`,
            }}
            aria-hidden="true"
          />
        ) : null}
      </div>
    )

    const stageOverlay =
      isPlacementActive && !isEnteringUsername ? (
        <div className={styles.stageOverlay}>
          <Text
            as="p"
            size="sm"
            color="inherit"
            className={styles.placementHint}
            data-testid="placement-hint"
            aria-hidden="true"
          >
            <CrosshairIcon size="small" />
            <span className={styles.placementHintText}>{placementHint}</span>
          </Text>
        </div>
      ) : null

    const controls = (
      <section className={styles.controlsContent} aria-labelledby={headingId}>
        <div className={styles.sectionHeader}>
          <Text
            ref={headingRef}
            id={headingId}
            as="h4"
            size="md"
            weight="semibold"
            color="default"
            className={styles.sectionHeading}
            tabIndex={-1}
          >
            {TAGGING_SECTION_HEADING}
          </Text>
          {totalTagCount > 0 ? (
            <Text color="subtle" size="sm">
              {totalTagCount} / {maxTagCount}
            </Text>
          ) : null}
        </div>

        {totalTagCount !== tags.length ? (
          <Text color="subtle" size="sm">
            {tags.length === 0
              ? 'No tags on this image.'
              : `${tags.length} ${tags.length === 1 ? 'tag' : 'tags'} on this image.`}
          </Text>
        ) : null}

        <VisuallyHidden as="output" aria-live="polite" aria-atomic="true">
          {cropRecoveryAnnouncement}
        </VisuallyHidden>

        <VisuallyHidden
          key={`move-${tagMoveAnnouncement.key}`}
          as="output"
          aria-live="polite"
          aria-atomic="true"
        >
          {tagMoveAnnouncement.text}
        </VisuallyHidden>

        <VisuallyHidden
          key={`position-${tagPositionAnnouncement.key}`}
          as="output"
          aria-live="polite"
          aria-atomic="true"
        >
          {tagPositionAnnouncement.text}
        </VisuallyHidden>

        {isEnteringUsername ? (
          <form
            className={styles.tagForm}
            aria-label="Instagram username"
            onSubmit={handleSubmit}
          >
            <Flex direction="column" align="stretch" gap="xs">
              <Input
                id={inputId}
                ref={inputRef}
                type="text"
                name="instagram-username"
                prefix="@"
                autoComplete="off"
                placeholder="username"
                value={username}
                maxLength={INSTAGRAM_USERNAME_MAX_LENGTH}
                disabled={disabled}
                aria-label="Instagram username"
                aria-invalid={inputError ? true : undefined}
                aria-describedby={
                  inputError
                    ? `${instagramLimitMessageId} ${inputErrorId}`
                    : instagramLimitMessageId
                }
                onChange={(event) => {
                  const normalizedUsername = normalizeInstagramUsername(
                    event.target.value,
                  )
                  setUsername(normalizedUsername)
                  setInputError(
                    normalizedUsername.length > 0 &&
                      !isValidInstagramUsername(normalizedUsername)
                      ? 'invalid'
                      : null,
                  )
                }}
              />
              {inputError ? (
                <Text id={inputErrorId} role="alert" color="critical" size="sm">
                  {inputError === 'duplicate'
                    ? 'Oops! User already tagged'
                    : 'Use only letters, numbers, periods, and underscores.'}
                </Text>
              ) : null}
              <div className={styles.tagFormFooter}>
                <Button
                  type="button"
                  variant="tertiary"
                  size="small"
                  aria-label="Cancel this tag"
                  disabled={disabled}
                  onClick={cancelPendingTag}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="small"
                  disabled={
                    disabled ||
                    reachedMaxTagCount ||
                    !isValidInstagramUsername(
                      normalizeInstagramUsername(username),
                    )
                  }
                >
                  Save Tag
                </Button>
              </div>
              <Text id={instagramLimitMessageId} color="subtle" size="sm">
                {INSTAGRAM_LIMIT_MESSAGE}
              </Text>
            </Flex>
          </form>
        ) : null}

        {tags.length > 0 ? (
          <ul className={styles.tagList} aria-label={TAGGING_SECTION_HEADING}>
            {tags.map((tag, index) => {
              const status =
                tag.state === 'outside'
                  ? 'Outside crop'
                  : tag.state === 'invalid'
                    ? 'Invalid position'
                    : null

              return (
                <li
                  className={styles.tagListItem}
                  data-highlighted={
                    highlightedAnchorIndex === tag.anchorIndex
                      ? 'true'
                      : undefined
                  }
                  key={`${tag.anchorIndex}-${tag.username}`}
                  onMouseEnter={() =>
                    setHoveredListAnchorIndex(tag.anchorIndex)
                  }
                  onMouseLeave={() =>
                    setHoveredListAnchorIndex((currentAnchorIndex) =>
                      currentAnchorIndex === tag.anchorIndex
                        ? null
                        : currentAnchorIndex,
                    )
                  }
                >
                  <div className={styles.tagIdentity}>
                    <span className={styles.tagIndex} aria-hidden="true">
                      {index + 1}
                    </span>
                    <div className={styles.tagDetails}>
                      <Link
                        external
                        color="inherit"
                        className={styles.profileLink}
                        href={getInstagramProfileUrl(tag.username)}
                        onFocus={() => setFocusedAnchorIndex(tag.anchorIndex)}
                        onBlur={() => clearFocusedTag(tag.anchorIndex)}
                      >
                        @{tag.username}
                      </Link>
                      {tag.state === 'outside' ? (
                        <Text
                          color="warning"
                          size="sm"
                          icon={<WarningIcon size="small" aria-hidden="true" />}
                        >
                          {status}
                        </Text>
                      ) : status ? (
                        <Text color="critical" size="sm">
                          {status}
                        </Text>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.tagActions}>
                    {tag.state !== 'invalid' ? (
                      <Button
                        ref={(button) => {
                          moveButtonRefs.current[index] = button
                        }}
                        type="button"
                        variant="tertiary"
                        size="small"
                        disabled={
                          disabled || placementDisabled || isPlacementActive
                        }
                        onClick={(event) => beginTagMove(event, tag, index)}
                        onFocus={() => setFocusedAnchorIndex(tag.anchorIndex)}
                        onBlur={() => clearFocusedTag(tag.anchorIndex)}
                      >
                        Move
                        <VisuallyHidden> @{tag.username}</VisuallyHidden>
                      </Button>
                    ) : null}
                    <IconButton
                      ref={(button) => {
                        removeButtonRefs.current[index] = button
                      }}
                      label={`Remove @${tag.username}`}
                      tooltip={`Remove @${tag.username}`}
                      variant="tertiary"
                      size="small"
                      disabled={disabled || isPlacementActive}
                      onClick={() => removeTag(tag.anchorIndex, index)}
                      onFocus={() => setFocusedAnchorIndex(tag.anchorIndex)}
                      onBlur={() => clearFocusedTag(tag.anchorIndex)}
                    >
                      <XIcon size="small" />
                    </IconButton>
                  </div>
                </li>
              )
            })}
          </ul>
        ) : null}

        {!isEnteringUsername ? (
          <div className={styles.tagActionRow}>
            {reachedMaxTagCount ? (
              <output className={styles.tagActionStatus}>
                <Text color="critical" size="sm">
                  Reached the tag limit.
                </Text>
              </output>
            ) : null}
            <Button
              ref={taggingActionRef}
              type="button"
              variant={
                isPlacementActive && !isMovingTag ? 'primary' : 'secondary'
              }
              size="medium"
              className={styles.tagAction}
              disabled={!canChoosePosition && !isPlacementActive}
              onClick={handleTaggingAction}
            >
              {!isPlacementActive ? <UserRoundPlusIcon /> : null}
              {isPlacementActive
                ? isMovingTag
                  ? 'Cancel move'
                  : 'Finish tagging'
                : 'Start tagging'}
            </Button>
          </div>
        ) : null}
      </section>
    )

    return (
      <>
        {children({
          imageOverlay,
          cropOverlay,
          stageOverlay,
          controls,
          isPlacementActive,
        })}
      </>
    )
  },
)

ComposerImageUserTagging.displayName = 'ComposerImageUserTagging'
